import React from "react";
import PropTypes from "prop-types";
import sprite from "../../containers/Tracking/assets/nativeIcons/icons.svg";

/**
 * QIMapInfoDialog - Reusable map info dialog component.
 *
 * Available in two forms:
 *   1. React component (QIMapInfoDialog) - for use in React render trees
 *   2. DOM factory (createMapInfoDialog) - for map overlays (OL/Mapbox/Google)
 *
 * Follows project BEM convention: qi-map-info-dialog_header (single underscore).
 */

// ─── React Components ───

/**
 * React component for rendering a map info dialog popover.
 *
 * @example
 * <QIMapInfoDialog
 *   header={{ icon: "bell-z", title: "Standstill", badge: "B 1234 XYZ" }}
 *   footer={<><time>3/5/2026</time><time>11:30 PM</time><data>23 min</data></>}
 * >
 *   <address>Jl. Sudirman - Jakarta Selatan</address>
 * </QIMapInfoDialog>
 */
export const QIMapInfoDialog = ({ header, children, footer, onClose, className = "" }) => {
  return (
    <article className={`qi-map-info-dialog_popover ${className}`.trim()} role="dialog">
      {header && (
        <header className="qi-map-info-dialog_header">
          <div className="qi-map-info-dialog_header_left">
            {header.icon && (
              <span className="icon-wrapper qi-map-info-dialog_header_icon">
                <svg className="icon">
                  <use href={`${sprite}#${header.icon}`} />
                </svg>
              </span>
            )}
            {header.title && (
              <h3 className="qi-map-info-dialog_header_title">{header.title}</h3>
            )}
          </div>
          {header.badge && (
            <span className="qi-map-info-dialog_header_badge">{header.badge}</span>
          )}
          {onClose && (
            <button
              className="qi-map-info-dialog_header_close"
              onClick={onClose}
              aria-label="Close"
              type="button"
            >
              ×
            </button>
          )}
        </header>
      )}

      {children && (
        <section className="qi-map-info-dialog_body">{children}</section>
      )}

      {footer && (
        <footer className="qi-map-info-dialog_footer">{footer}</footer>
      )}
    </article>
  );
};

QIMapInfoDialog.propTypes = {
  header: PropTypes.shape({
    icon: PropTypes.string,
    title: PropTypes.string,
    badge: PropTypes.string,
  }),
  children: PropTypes.node,
  footer: PropTypes.node,
  onClose: PropTypes.func,
  className: PropTypes.string,
};

// ─── DOM Factory (for map overlays) ───

/**
 * Creates an SVG icon element from the native icons sprite.
 * @param {string} name - Icon ID in the sprite
 * @param {string} [className=""] - Additional CSS class
 * @param {number} [size=16] - Icon size in px
 * @returns {HTMLElement}
 */
export function createSpriteIcon(name, className = "", size = 16) {
  const wrapper = document.createElement("span");
  wrapper.className = `icon-wrapper ${className}`.trim();

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "icon");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));

  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  use.setAttributeNS("http://www.w3.org/1999/xlink", "href", `${sprite}#${name}`);
  svg.appendChild(use);
  wrapper.appendChild(svg);

  return wrapper;
}

/**
 * Creates the arrow/caret SVG element pointing downward.
 * @returns {HTMLElement}
 */
function createArrow() {
  const el = document.createElement("div");
  el.className = "qi-map-info-dialog_arrow";
  el.setAttribute("aria-hidden", "true");
  el.innerHTML =
    '<svg width="16" height="8" viewBox="0 0 16 8"><polygon points="0,0 16,0 8,8"/></svg>';
  return el;
}

/**
 * Creates a map info dialog (pill + popover) as raw DOM elements.
 * For use with OL Overlay, Mapbox Marker, or Google OverlayView.
 *
 * @param {Object} options
 * @param {Object} options.pill - Pill label config: { icon, text, prefix }
 * @param {Object} options.header - Header config: { icon, title, badge }
 * @param {string|HTMLElement} options.body - Body content (text or DOM element)
 * @param {Array} [options.footer] - Footer items: [{ text, className, tag }]
 * @param {Function} [options.onOpen] - Callback when popover opens
 * @param {Function} [options.onClose] - Callback when popover closes
 * @param {string} [options.className] - Additional class for the container
 * @returns {{ container: HTMLElement, open: Function, close: Function }}
 */
export function createMapInfoDialog(options) {
  const {
    pill,
    header,
    body,
    footer,
    onOpen,
    onClose,
    className = "",
  } = options;

  const container = document.createElement("div");
  container.className = `qi-map-info-dialog ${className}`.trim();

  // ── Popover ──
  const popoverEl = document.createElement("article");
  popoverEl.className = "qi-map-info-dialog_popover";
  popoverEl.setAttribute("role", "dialog");
  popoverEl.style.display = "none";

  // Header
  if (header) {
    const headerEl = document.createElement("header");
    headerEl.className = "qi-map-info-dialog_header";

    const headerLeft = document.createElement("div");
    headerLeft.className = "qi-map-info-dialog_header_left";

    if (header.icon) {
      headerLeft.appendChild(createSpriteIcon(header.icon, "qi-map-info-dialog_header_icon"));
    }

    if (header.title) {
      const titleEl = document.createElement("h3");
      titleEl.className = "qi-map-info-dialog_header_title";
      titleEl.textContent = header.title;
      headerLeft.appendChild(titleEl);
    }

    headerEl.appendChild(headerLeft);

    if (header.badge) {
      const badgeEl = document.createElement("span");
      badgeEl.className = "qi-map-info-dialog_header_badge";
      badgeEl.textContent = header.badge;
      headerEl.appendChild(badgeEl);
    }

    popoverEl.appendChild(headerEl);
  }

  // Body
  if (body) {
    const bodyEl = document.createElement("section");
    bodyEl.className = "qi-map-info-dialog_body";
    if (typeof body === "string") {
      // Wrap address text in <address> for semantic meaning
      const addressEl = document.createElement("address");
      addressEl.textContent = body;
      bodyEl.appendChild(addressEl);
    } else {
      bodyEl.appendChild(body);
    }
    popoverEl.appendChild(bodyEl);
  }

  // Footer
  if (footer && footer.length) {
    const footerEl = document.createElement("footer");
    footerEl.className = "qi-map-info-dialog_footer";
    footer.forEach((item) => {
      const tag = item.tag || "span";
      const el = document.createElement(tag);
      el.className = item.className || "";
      el.textContent = item.text;
      footerEl.appendChild(el);
    });
    popoverEl.appendChild(footerEl);
  }

  const popoverArrow = createArrow();
  popoverArrow.style.display = "none";

  // ── Pill ──
  const pillEl = document.createElement("button");
  pillEl.className = "qi-map-info-dialog_pill";
  pillEl.setAttribute("type", "button");
  pillEl.setAttribute("aria-label", "Show details");

  if (pill.prefix) {
    const prefixEl = document.createElement("span");
    prefixEl.className = "qi-map-info-dialog_pill_prefix";
    prefixEl.textContent = pill.prefix;
    pillEl.appendChild(prefixEl);
  }

  if (pill.icon) {
    pillEl.appendChild(createSpriteIcon(pill.icon, "qi-map-info-dialog_pill_icon"));
  }

  if (pill.text) {
    const textEl = document.createElement("span");
    textEl.className = "qi-map-info-dialog_pill_text";
    textEl.textContent = pill.text;
    pillEl.appendChild(textEl);
  }

  const pillArrow = createArrow();

  // Assemble
  container.appendChild(popoverEl);
  container.appendChild(popoverArrow);
  container.appendChild(pillEl);
  container.appendChild(pillArrow);

  // ── Toggle logic ──
  const open = () => {
    popoverEl.style.display = "";
    popoverArrow.style.display = "";
    pillEl.style.display = "none";
    pillArrow.style.display = "none";
    if (typeof onOpen === "function") onOpen();
  };

  const close = () => {
    popoverEl.style.display = "none";
    popoverArrow.style.display = "none";
    pillEl.style.display = "";
    pillArrow.style.display = "";
    if (typeof onClose === "function") onClose();
  };

  container._closePopover = close;

  pillEl.addEventListener("click", (e) => {
    e.stopPropagation();
    open();
  });

  popoverEl.addEventListener("click", (e) => {
    e.stopPropagation();
  });

  return { container, open, close };
}